import React from 'react';

interface AssetLogoProps {
  symbol: string;
  name?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const NON_EQUITY_SUFFIXES = ['.FOREX', '.INDX', '.CC', '.COMM'];

/**
 * Derive the backend proxy URL for a company logo.
 * Symbol format: TICKER.EXCHANGE (e.g. SLM.JSE, AAPL.US)
 * Proxy sets Cache-Control: public, max-age=5184000 (60 days) so the
 * browser caches the image — zero network requests after first load.
 */
const logoProxyUrl = (symbol: string): string | null => {
  const parts = symbol.split('.');
  if (parts.length < 2) return null;
  const ticker = parts[0];
  const exchange = parts[parts.length - 1].toUpperCase();
  return `/api/v1/market/logos/${exchange}/${ticker}`;
};

const AssetLogo: React.FC<AssetLogoProps> = ({ symbol, name, size = 'md', className = '' }) => {
  const [error, setError] = React.useState(false);

  const sizeClasses = {
    xs: 'w-4 h-4 text-[8px]',
    sm: 'w-6 h-6 text-[10px]',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base',
    xl: 'w-16 h-16 text-lg',
  };
  const sizeClass = sizeClasses[size];

  const upper = symbol.toUpperCase();
  const hasLogo = !NON_EQUITY_SUFFIXES.some(s => upper.endsWith(s));
  const src = hasLogo ? logoProxyUrl(symbol) : null;

  if (src && !error) {
    return (
      <img
        src={src}
        alt={`${name || symbol} logo`}
        className={`${sizeClass} object-contain rounded bg-white/5 p-0.5 ${className}`}
        onError={() => setError(true)}
        loading="lazy"
      />
    );
  }

  // Fallback: letter avatar
  const firstLetter = symbol.charAt(0).toUpperCase();
  const colorVariants = [
    'from-teal-500 to-blue-600',
    'from-purple-500 to-pink-600',
    'from-orange-500 to-red-600',
    'from-green-500 to-teal-600',
    'from-blue-500 to-indigo-600',
    'from-pink-500 to-rose-600',
  ];
  const gradient = colorVariants[symbol.charCodeAt(0) % colorVariants.length];

  return (
    <div
      className={`${sizeClass} bg-gradient-to-br ${gradient} rounded flex items-center justify-center font-bold text-white ${className}`}
      aria-label={`${name || symbol} logo`}
    >
      {firstLetter}
    </div>
  );
};

export default AssetLogo;
